import {
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  Divider,
  EXTRAS_MENU_TOOLTIP_PROPS,
  ExportMenuItems,
  FormControlSizes,
  IconButton,
  IconName,
  MixpanelEvent,
  POSITION,
  Toggle,
  columnTypes,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterGroupingToggling,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type BlotterGroupingDefinition,
  type MarketAccountTypeEnum,
  type Position,
  type WebsocketRequest,
} from '@talos/kyoko';
import { useMemo } from 'react';

import { CONTROL_TOOLTIPS } from '@src/containers/Blotters/tooltips';
import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import { DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS } from '@talos/kyoko';
import type { StateUpdatedEvent } from 'ag-grid-community';
import { useFeatureFlag } from '../../../../hooks';
import { defaultSpotBlotterColumns, defaultSpotBlotterFilter } from '../defaults';
import { GROUP_COL_WIDTH, POSITIONS_BLOTTER_CONVERSION_TOLERANCE } from '../tokens';
import type { ClonePositionsTabFunc, SpotPositionsTabProps } from '../types';
import { usePositionsMenu } from '../usePositionsMenu';
import { useShowAdjustMarketAccountPositionButton } from '../useShowAdjustMarketAccountPositionButton';
import { usePositionsSpotColumns } from './usePositionsSpotColumns';
import { usePositionsSpotFilter } from './usePositionsSpotFilter';

export interface PositionsSpotBlotterParams {
  blotterID: string;
  tab: SpotPositionsTabProps;
  onUpdateTab: (tab: SpotPositionsTabProps) => void;
  initialIsOpen?: boolean;
  onCloneTab: ClonePositionsTabFunc;
  tag?: string;
  /**
   * If you want the blotter to be limited to only showing one type of Market Accounts (for example Customer ones),
   * apply that here.
   *
   * The option makes the blotter set a hidden Filter on Position.MarketAccount.MarketAccountType which cannot be seen or removed.
   */
  limitToMarketAccountType?: MarketAccountTypeEnum;
}

type SpotGroupings = 'Asset' | 'Market Account';
type GroupablePositionColumns = keyof Position;
const GROUPINGS: BlotterGroupingDefinition<SpotGroupings, GroupablePositionColumns>[] = [
  {
    key: 'Asset',
    label: 'By Asset',
    rowGroupColumns: ['Asset'],
    mixpanelEvent: MixpanelEvent.GroupAssets,
    buttonDataTestId: 'by-asset-button',
  },
  {
    key: 'Market Account',
    label: 'By Market Account',
    rowGroupColumns: ['MarketAccount'],
    mixpanelEvent: MixpanelEvent.GroupAccounts,
    buttonDataTestId: 'by-market-account-button',
  },
];

export function PositionsSpotBlotter({
  blotterID,
  tab,
  onUpdateTab,
  initialIsOpen,
  onCloneTab,
  tag,
  limitToMarketAccountType,
}: PositionsSpotBlotterParams) {
  const mixpanel = useMixpanel();
  const { enableLegacyUpdateBalances } = useFeatureFlag();

  const defaultBlotterColumns = tab.defaultColumns ?? defaultSpotBlotterColumns;
  const defaultFilter = tab.defaultFilter ?? defaultSpotBlotterFilter;
  const showZeroBalances = tab.showZeroBalances ?? false;

  const defaultColumns = usePositionsSpotColumns(defaultBlotterColumns);

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });

  const filterResults = usePositionsSpotFilter({
    persistedBlotterTable,
    defaultFilter,
    limitToMarketAccountType,
  });

  const { filterBuilderProps } = filterResults;

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    onGroupingStateUpdated(event);
  });

  const { homeCurrency } = useDisplaySettings();
  const updateShowZeroBalances = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowZeroBalances);
    onUpdateTab({ ...tab, showZeroBalances: value });
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const positionsMenu = usePositionsMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
    tabType: 'Spot',
    showOpenTransactionsMenuItem: true,
  });

  const columnsWithMenu = useMemo(
    () => [...defaultColumns, ...positionsMenu.columns],
    [defaultColumns, positionsMenu.columns]
  );

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        title: 'Grouping',
        editable: false,
        width: GROUP_COL_WIDTH,
        hide: false,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const baseRequest = useMemo(
    () => ({
      name: POSITION,
      tag: blotterID,
      ShowZeroBalances: showZeroBalances,
      EquivalentCurrency: homeCurrency,
      Tolerance: POSITIONS_BLOTTER_CONVERSION_TOLERANCE,
    }),
    [showZeroBalances, homeCurrency, blotterID]
  );
  const request = useMemo(
    () => ({
      ...baseRequest,
      ...removeEmptyFilters(filterResults.filter),
    }),
    [baseRequest, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable<WebsocketRequest, Position>({
    request,
    rowID: 'rowID' satisfies keyof Position,
    persistence: persistedBlotterTable,
    columns: columnsWithMenu,
    gridOptions: {
      onStateUpdated: handleGridStateUpdated,
      groupRemoveSingleChildren: true,
      showOpenedGroup: true,
      autoGroupColumnDef,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      groupDisplayType: 'singleColumn',
      getContextMenuItems: positionsMenu.getContextMenuItems,
    },
    exportOptions: {
      name: 'PositionsSpot',
      tabLabel: tab.label,
    },
  });

  const { expandAllGroups, collapseAllGroups, setRowGroupColumns } = blotterTable;

  const { onStateUpdated: onGroupingStateUpdated, groupingToggleButtons } = useBlotterGroupingToggling({
    blotterName: 'positions spot',
    setRowGroupColumns,
    groupings: GROUPINGS,
  });

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab?.();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();
  const showAdjustMarketAccountPositionButton = useShowAdjustMarketAccountPositionButton();

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        accordionBodyPrefix={groupingToggleButtons}
        portalId={BLOTTER_TABLE_FILTERS_CONTAINER_ID}
        prefix={
          <>
            <IconButton
              icon={IconName.ListExpand}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={expandAllGroups}
              data-testid="expand-all-button"
            />
            <IconButton
              icon={IconName.ListCollapse}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={collapseAllGroups}
              data-testid="collapse-all-button"
            />
          </>
        }
        suffix={
          <>
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              <Toggle
                tooltip={CONTROL_TOOLTIPS.showZeroBalances}
                tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                size={FormControlSizes.Small}
                checked={showZeroBalances}
                onChange={updateShowZeroBalances}
                label="Show Zero Balances"
              />
              <Divider orientation="horizontal" />
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>

              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
              {showAdjustMarketAccountPositionButton && (
                <Button
                  startIcon={IconName.Pencil}
                  variant={ButtonVariants.Default}
                  size={FormControlSizes.Small}
                  data-testid="update-balances-button"
                  onClick={() => positionsMenu.openUpdateBalancesDialog()}
                >
                  {enableLegacyUpdateBalances ? 'Update Balances' : 'Adjust Market Account Position'}
                </Button>
              )}
            </BlotterTableExtrasMenu>
          </>
        }
      />
      <BlotterTable {...blotterTable} />
      {positionsMenu.dialogs}
    </>
  );
}
